import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek.js'
import timezone from 'dayjs/plugin/timezone.js'
import utc from 'dayjs/plugin/utc.js'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(isoWeek)

export namespace DateHelper {
  export function getNow(): Date {
    return new Date()
  }

  export function addDays(date: Date, days: number): Date {
    const dateUpdated = new Date(date.getTime())
    const hours = days * 24
    const minutes = hours * 60
    const seconds = minutes * 60
    const milliseconds = seconds * 1000

    dateUpdated.setTime(dateUpdated.getTime() + milliseconds)

    return dateUpdated
  }

  export function addMinutes(date: Date, minutes: number): Date {
    const dateUpdated = new Date(date.getTime())
    const seconds = minutes * 60
    const milliseconds = seconds * 1000

    dateUpdated.setTime(dateUpdated.getTime() + milliseconds)

    return dateUpdated
  }

  export function isBefore(dateBefore: Date, dateAfter: Date): boolean {
    return dateBefore < dateAfter
  }

  export function toAiDate(date: Date | string, timezoneUser = 'UTC') {
    return (
      dayjs(date).tz(timezoneUser).format('dddd, D [of] MMMM YYYY [at] HH:mm') +
      ` (${timezoneUser})`
    )
  }

  export function toGmailDate(date: Date | string, timezoneUser = 'UTC') {
    return dayjs(date)
      .tz(timezoneUser)
      .format('ddd, D [of] MMMM YYYY [at] HH:mm')
  }

  export function toOutlookDate(date: Date | string, timezoneUser = 'UTC') {
    return dayjs(date).tz(timezoneUser).format('D MMMM YYYY HH:mm')
  }

  export function toTableDate(date: Date) {
    return dayjs(date).format('MMM DD HH:mm:ss.SSS')
  }

  export function toHumanDate(date: Date) {
    return dayjs(date).format('DD MMM YYYY')
  }

  export function toHumanTime(date: Date) {
    return dayjs(date).format('HH:mm')
  }

  export function toHumanTimePrecise(date: Date) {
    return dayjs(date).format('HH:mm:ss')
  }

  export function toHumanTimePreciseMilliseconds(date: Date) {
    return dayjs(date).format('HH:mm:ss.SSS')
  }

  export function toSlackTimeStyle(date: Date) {
    if (!date) {
      return ''
    }

    return dayjs(date).format('h:mm A')
  }

  export function toAsanaStyle(date: Date) {
    if (!date) {
      return ''
    }

    const now = dayjs().startOf('day')
    const dateFormatted = dayjs(date).startOf('day')

    const isSameYear = dateFormatted.isSame(now, 'year')
    const isToday = dateFormatted.isSame(now, 'day')
    const isTomorrow = dateFormatted.isSame(now.add(1, 'day'), 'day')
    const isYesterday = dateFormatted.isSame(now.add(-1, 'day'), 'day')
    const isWithinWeek =
      dateFormatted.diff(now, 'days') < 7 && dateFormatted.isAfter(now)

    if (isToday) {
      return 'Today'
    } else if (isTomorrow) {
      return 'Tomorrow'
    } else if (isYesterday) {
      return 'Yesterday'
    } else if (isWithinWeek) {
      return dateFormatted.format('dddd')
    } else if (isSameYear) {
      return dateFormatted.format('DD MMM')
    }

    dateFormatted.format('DD MMM YYYY')
  }

  export function toNotionStyle(date: Date) {
    if (!date) {
      return ''
    }

    const now = dayjs()
    const input = dayjs(date)

    const diffInMinutes = now.diff(input, 'minute')
    const diffInHours = now.diff(input, 'hour')
    const diffInDays = now.diff(input, 'day')

    if (diffInMinutes < 1) {
      return 'now'
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`
    } else if (diffInHours < 24) {
      return `${diffInHours}h ago`
    } else if (diffInDays === 1) {
      return 'yesterday'
    } else if (diffInDays < 7) {
      return `${diffInDays}d ago`
    } else {
      return input.format('DD MMM YYYY')
    }
  }

  export function toWhatsappStyle(date: Date) {
    if (!date) {
      return ''
    }

    const now = dayjs().startOf('day')
    const dateFormatted = dayjs(date).startOf('day')
    const isToday = dateFormatted.isSame(now, 'day')

    if (isToday) {
      return toSlackTimeStyle(date)
    }

    return toAsanaStyle(date)
  }

  export function getDifferenceInMinutes(dateOne: Date, dateTwo: Date): number {
    const differenceInMilliseconds = Math.abs(
      dateTwo.getTime() - dateOne.getTime(),
    )

    const differenceInMinutes = differenceInMilliseconds / (1000 * 60)

    return Math.floor(differenceInMinutes)
  }

  export function getDifferenceInDays(
    startDate: string,
    endDate: string,
    options?: {
      includeBusinessDays?: boolean
    },
  ): number {
    const includeBusinessDays = options?.includeBusinessDays ?? true

    const start = dayjs(startDate).startOf('day')
    const end = dayjs(endDate).startOf('day')

    if (!includeBusinessDays) {
      let count = 0
      let current = start

      while (current.isBefore(end, 'day')) {
        const day = current.day()
        if (day !== 0 && day !== 6) {
          count++
        }
        current = current.add(1, 'day')
      }

      return count
    }

    return end.diff(start, 'day')
  }

  export function getDaysBetween(startDate: string, endDate: string): string[] {
    const start = dayjs(startDate)
    const end = dayjs(endDate)

    const days: string[] = []

    for (let i = 0; i <= end.diff(start, 'day'); i++) {
      days.push(start.add(i, 'day').format('YYYY-MM-DD'))
    }

    return days
  }

  export function getWeekStartISO(date: Date = new Date()): string {
    return dayjs.utc(date).startOf('isoWeek').format('YYYY-MM-DD')
  }

  export function toLocalTimestamp(date: Date, localTimezone: string): number {
    return dayjs.tz(date, localTimezone).unix()
  }
}
